/**
 * /table · route + verification primitives.
 *
 * Maps backend booking_cta / destinationType / resolver platform onto
 * the canonical routing ladder used everywhere on the surface.
 *
 * TIER REGISTRY (8 Feb 2026 refactor · founder direction):
 * Each tier is a STANDALONE entry. Cards (restaurant / nightlife)
 * pick which tier keys apply via a LADDER constant. The same tier
 * registry powers every card type — restaurant card uses the
 * RESTAURANT_LADDER, nightclub card uses the NIGHTLIFE_LADDER, both
 * read from the same TIER table.
 *
 * The doctrine fix that drove this refactor (LAB by Sergi Arola card):
 *   Google Reserve must NEVER read as "the venue's own link."
 *   It's an intermediary rail Google operates. Surface it honestly
 *   in its own tier ("via Google Reserve"), never under "Direct ·
 *   Instant · The venue's own booking link".
 *
 *   For hard-to-book / Michelin / nightlife rows, the recommended
 *   HERO is Concierge® — door access and starred tables are
 *   relationship work, not link-out work.
 *
 * The classifier is purely deterministic. There is no LLM involved.
 */

export const ROUTE = {
    instant: {
        key: "instant",
        label: "Direct · Instant",
        icon: "⚡",
        color: "var(--t-verified)",
        wash: "var(--t-verified-wash)",
        line: "The venue's own booking link.",
        cta: "Reserve instantly",
    },
    // NEW 8 Feb 2026 · Google Reserve gets its own honest tier · NEVER
    // labelled "the venue's own link" (LAB doctrine fix).
    google_reserve: {
        key: "google_reserve",
        label: "via partner reservation",
        icon: "⚡",
        color: "var(--t-pending)",
        wash: "var(--t-pending-wash)",
        line: "An external reservation rail — we surface it when the venue has opted in.",
        cta: "Reserve",
    },
    call: {
        key: "call",
        label: "Direct · Call",
        icon: "☎",
        color: "var(--t-pending)",
        wash: "var(--t-pending-wash)",
        line: "The phone number the venue has published — your Concierge® will call to secure the table.",
        cta: "Call to reserve · by Concierge®",
    },
    contact: {
        key: "contact",
        label: "Direct · Contact",
        icon: "✉",
        color: "var(--t-slate)",
        wash: "var(--t-slate-wash)",
        line: "The venue's own website / email.",
        cta: "Book on their site",
    },
    concierge: {
        key: "concierge",
        label: "Concierge® · by hand",
        icon: "✦",
        color: "var(--t-brass)",
        wash: "var(--t-brass-wash)",
        line: "Hard-to-book tables are a relationship — we secure them directly with the venue.",
        cta: "Request via Concierge®",
    },
    // NIGHTLIFE-specific tiers (added 8 Feb 2026 · reuses the same
    // honest-tier registry · powers the nightclub card without a
    // parallel rebuild).
    guest_list: {
        key: "guest_list",
        label: "Guest list",
        icon: "✎",
        color: "var(--t-pending)",
        wash: "var(--t-pending-wash)",
        line: "The club's own list — where it publishes one.",
        cta: "Request guest-list spot",
    },
    table_service: {
        key: "table_service",
        label: "Table · bottle service",
        icon: "◆",
        color: "var(--t-slate)",
        wash: "var(--t-slate-wash)",
        line: "The venue's own table-booking channel.",
        cta: "Reserve a table",
    },
};

// Restaurant-card tier order (top = best, bottom = fallback). The
// Drawer iterates this array and shows each tier marked "available"
// or "not available for this venue".
export const RESTAURANT_LADDER = [
    "instant",         // venue's-own booking link via a verified platform
    "google_reserve",  // intermediary · honest separate tier
    "call",
    "contact",
    "concierge",
];

// Nightlife-card tier order — recommended HERO is Concierge® at the
// top because door access is relationship work.
export const NIGHTLIFE_LADDER = [
    "concierge",       // recommended HERO for clubs · door = relationship
    "guest_list",
    "table_service",
    "contact",
];

// Venue's-own-link platforms (the venue PUBLISHED an embed/booking
// page on these). NOTE · Google Reserve is INTENTIONALLY excluded
// here · it has its own honest tier above. Adding it back would
// re-introduce the LAB doctrine bug.
const INSTANT_PLATFORMS = [
    "sevenrooms", "opentable", "resy", "tablecheck", "tock", "tabelog",
    "thefork", "lafourchette", "quandoo", "bookatable",
    // India operators
    "eazydiner", "dineout", "swiggy dineout", "zomato",
];

const GOOGLE_RESERVE_TOKENS = [
    "google reserve",
    "reserve with google",
    "reserve on google",
    "google.com/maps/reserve",
];

/**
 * Detect a Google Reserve route from venue card data. Used to keep
 * Google Reserve out of the `instant` bucket — it gets its own tier.
 */
export function isGoogleReserveRoute(venue) {
    if (!venue) return false;
    const haystack = [
        venue.route_via,
        venue.booking_cta,
        venue.booking_platform,
        venue.reservation_url,
    ]
        .filter((v) => typeof v === "string" && v.length > 0)
        .map((v) => v.toLowerCase())
        .join(" | ");
    return GOOGLE_RESERVE_TOKENS.some((t) => haystack.includes(t));
}

/**
 * "Relationship beats self-service" predicate · narrower as of
 * 10 Feb 2026 PM founder ruling.
 *
 * DOCTRINE (founder ruling · 10 Feb 2026 PM · BC-023 · inverts the
 * 8 Feb 2026 ruling):
 *   "The michelin has to go to their official booking and concierge
 *    is also there to help in brass · the customer has a choice ·
 *    and we have to open to every single booking agent it connects
 *    to · fork, resy, nomad, chope, swiggy whatever."
 *
 * Translation:
 *   • Where the venue ships a VERIFIED self-service rail (TheFork /
 *     Resy / Google Reserve / SevenRooms / Chope / Swiggy Dineout /
 *     TableCheck / OpenTable / Tock / EazyDiner / etc.), THAT is the
 *     primary CTA · even for Michelin · even for anchored rows.
 *   • Concierge® stays as a SECONDARY brass CTA on every card · the
 *     member always has the choice · "or have Concierge® reserve it
 *     for you".
 *   • Only when there is NO verified rail does Concierge® become the
 *     primary (it's the only honest path).
 *
 * The previous predicate (Michelin/anchored → concierge primary)
 * created a meaningless ceremony for venues like Jamavar Dubai
 * (Michelin 1★ but bookable instantly via Google Reserve) — clicking
 * "Reserve" generated an LC ticket the desk would then have to
 * forward back to the same Google Reserve link. We do not wrap
 * theater around a working self-service rail.
 *
 * Categories that STILL pass this gate (where Concierge® genuinely
 * adds value · returned as the primary recommendation):
 *   • Nightlife (door access IS relationship work · always)
 *   • Explicit backend flag (`venue.hard_to_book === true`) set by
 *     the desk when they KNOW a row needs hand-routing (rare; for
 *     overlays the desk has flagged as "Concierge® only").
 *
 * Michelin / anchored / verified-2026 are NO LONGER auto-gated to
 * Concierge® · they fall through to whatever rail is verified.
 */
export function isHardToBook(venue) {
    if (!venue) return false;
    const arc = venue.archive_texture || {};
    // Nightlife AND bars · door access / bar seating is relationship work
    // (BC-036 · 10 Feb 2026 PM · bars adopted the same Card-in-Chat
    // contract · Concierge® is the relationship-primary CTA there too)
    // DB canonical is `bar` (singular) · `bars` accepted as alias.
    const cat = (
        arc.category_render_type
        || venue.category_render_type
        || ""
    ).toLowerCase();
    if (cat === "nightlife" || cat === "bar" || cat === "bars") return true;
    // Explicit backend signal · desk-set "Concierge® only" flag
    if (venue.hard_to_book === true) return true;
    return false;
}

/**
 * Alias · "isRelationshipCategory" reads truer to the doctrine the
 * predicate encodes. Both names point to the same gate so existing
 * call sites + tests keep working.
 */
export const isRelationshipCategory = isHardToBook;

/**
 * Map a backend search/resolver-card to one of the TIER keys.
 * Reads `route` first (set explicitly by the backend's `_enrich_one`
 * 4-tier classifier), with a `booking_cta` fallback for older cards.
 *
 * 8 Feb 2026 · Google Reserve detection routed to its own tier.
 */
export function classifyRoute(venue) {
    if (!venue) return "concierge";
    // Google Reserve gets its own honest tier · NEVER labelled
    // "the venue's own link".
    if (isGoogleReserveRoute(venue)) return "google_reserve";
    // Authoritative: backend sets venue.route to one of
    //   "instant" | "call" | "contact" | "concierge"
    const r = (venue.route || "").toLowerCase();
    if (r === "instant" || r === "call"
        || r === "contact" || r === "concierge") {
        return r;
    }
    // Legacy fallback (pre-v3 cards): derive from booking_cta.
    const cta = (venue.booking_cta || "").toLowerCase();
    if (cta.startsWith("reserve on ")) return "instant";
    if (cta === "call to reserve") return "call";
    if (cta === "visit website" || cta === "book on their site"
        || cta === "reserve via whatsapp") return "contact";
    return "concierge";
}

/**
 * Recommended HERO tier · which tier wears the "recommended" badge.
 *
 *   • Hard-to-book / Michelin / nightlife → Concierge® (doctrine
 *     8 Feb 2026 · door & star tables are relationship work)
 *   • Otherwise → whatever classifyRoute returns (best-available)
 */
export function recommendedTier(venue) {
    if (isHardToBook(venue)) return "concierge";
    return classifyRoute(venue);
}

/**
 * Build the ladder rows for a given venue + ladder array. Each row
 * is `{ key, tier, available, isRecommended }`. The drawer iterates
 * this. Same shape works for restaurant + nightclub + any future
 * card type — they just pick a different ladder constant.
 *
 * `available` semantics:
 *   • The classified route is always available.
 *   • Tiers more conservative than the classified route are available
 *     as honest fallbacks (the cockpit doctrine: any concierge can
 *     always route by hand).
 *   • Tiers MORE optimistic than what we have evidence for are
 *     marked unavailable.
 *
 * Special case · `concierge` is ALWAYS available — the cockpit
 * fallback is universal.
 */
export function classifyTiers(venue, ladder = RESTAURANT_LADDER) {
    const classified = classifyRoute(venue);
    const recommended = recommendedTier(venue);
    // Map each tier key to its "depth" within the ladder so we can
    // mark conservative siblings as available.
    const depth = Object.fromEntries(ladder.map((k, i) => [k, i]));
    const classifiedDepth = depth[classified] ?? ladder.length;

    return ladder.map((k) => {
        const tier = ROUTE[k];
        // Concierge® is always available — universal cockpit fallback.
        if (k === "concierge") {
            return {
                key: k,
                tier,
                available: true,
                isRecommended: recommended === k,
            };
        }
        // Google Reserve is available iff the venue is actually
        // route-via Google Reserve (we never invent the rail).
        if (k === "google_reserve") {
            return {
                key: k,
                tier,
                available: isGoogleReserveRoute(venue),
                isRecommended: recommended === k,
            };
        }
        // Default: a tier is available when the classified route is
        // at or above it in the ladder (i.e., we have at least as
        // much evidence as this tier requires).
        const myDepth = depth[k] ?? ladder.length;
        return {
            key: k,
            tier,
            available: myDepth >= classifiedDepth,
            isRecommended: recommended === k,
        };
    });
}

/**
 * Pull the verified routing surface name (best-effort) from a card.
 * Returns null when we'd be inventing it.
 */
export function routeVia(venue) {
    if (!venue) return null;
    // Backend may publish venue.route_via for the new 4-tier shape
    if (venue.route_via) return venue.route_via;
    const cta = venue.booking_cta || "";
    if (cta.startsWith("Reserve on ")) {
        return cta.replace("Reserve on ", "").trim();
    }
    if (cta === "Call to reserve · by Concierge®" || cta === "Call to reserve")
        return "Direct phone line";
    if (cta === "Visit Website" || cta === "Book on their site")
        return "Venue site";
    if (cta === "Reserve via WhatsApp") return "Venue WhatsApp";
    return null;
}

/**
 * Verified-at relative phrase. Stays deterministic (no LLM).
 * Falls back to the literal evidence_source.verified_at when relative
 * formatting would lose precision.
 */
export function verifiedAt(venue) {
    const iso = venue?.evidence_source?.verified_at;
    if (!iso) return "verified live";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "verified live";
    const diffH = Math.max(1, Math.round((Date.now() - then) / 3.6e6));
    if (diffH < 24) return `verified ${diffH} hr${diffH === 1 ? "" : "s"} ago`;
    const days = Math.round(diffH / 24);
    if (days <= 30) return `verified ${days} day${days === 1 ? "" : "s"} ago`;
    return "verified · live";
}

/** Price display per the reference: £···, ££··, £££·, ££££. */
export function priceDisplay(level) {
    if (level === null || level === undefined) return null;
    const n = Math.max(1, Math.min(4, level));
    const dollars = "£".repeat(n);
    return (dollars + "····").slice(0, 4);
}

/**
 * Cuisine-first concise typographic line for cards. Uses verified
 * Places cuisine_tags (never LLM-derived). Falls back to omitting.
 */
export function cuisineLine(venue) {
    const tags = Array.isArray(venue?.cuisine_tags) ? venue.cuisine_tags : [];
    if (tags.length === 0) return null;
    return tags.slice(0, 2).join(" · ");
}


/**
 * Item 7b · Provider widget embed URL transformer.
 *
 * Two layers:
 *   1. **Known-no-embed list** — providers that publish
 *      `X-Frame-Options` (or equivalent CSP) and refuse to be
 *      iframed. For these we return null IMMEDIATELY so the
 *      BookingConfirmation skips the iframe attempt entirely and
 *      shows the "Complete on {provider} ↗" new-tab button. This is
 *      the founder doctrine fix (4 Jun 2026 evening ear-test):
 *      "never even try to embed a known-no-embed provider; a
 *      visible 'refused to connect' icon is never acceptable —
 *      it's worse than no embed."
 *
 *   2. **Mobile / iOS short-circuit (5 Jun 2026 morning · Item 19
 *      iOS polish)** — on phones and iPads, Safari's ITP frequently
 *      delivers a blank white iframe for providers' booking widgets
 *      (Resy, OpenTable etc.) even when the X-Frame-Options header
 *      would otherwise allow them. Rather than show that blank
 *      square, on mobile we return null and the popup goes straight
 *      to the new-tab handoff — same booking, no broken pane.
 *      Desktop UX is unchanged.
 *
 *   3. **Embeddable providers** — verified live as iframe-friendly.
 *      We build the deterministic widget URL (Resy → widgets.resy.com,
 *      OpenTable → reserve URL, etc.) and pass through date + seats
 *      where the provider honours URL params.
 *
 * For everything else (Tabelog, Tock, TheFork, Quandoo, EazyDiner,
 * Dineout, Zomato, Swiggy Dineout, Bookatable, Google Reserve, etc.)
 * we return null too · they cleanly fall to the new-tab button.
 *
 * Honesty rule: if the deeplink doesn't already carry the venue
 * identifier we need to build the widget URL, we return null and the
 * caller MUST fall back. Better an honest tab-out than a broken iframe.
 */

// Providers that refuse to be iframed (confirmed live).
// Adding a name here is an explicit "skip the iframe, go straight
// to the new-tab button" instruction.
//
// Item 21d · founder ear-test 5 Jun 2026 (Suprnova): Google Reserve
// → new-tab handoff is INTENTIONAL · Google Reserve sets
// X-Frame-Options: SAMEORIGIN on its Maps booking action URL, same
// as TableCheck. Don't try to "fix" this with a transparent overlay
// or a hidden frame · the new-tab handoff is the right behaviour.
/**
 * KNOWN_NO_EMBED · platforms whose iframes are KNOWN to refuse
 * mounting (X-Frame-Options DENY / CSP frame-ancestors strict). For
 * these we short-circuit · render the new-tab fallback button
 * immediately rather than showing a 5.5-sec blank flash.
 *
 * The default is OPPOSITE · every other platform tries to embed
 * first · the BookingSheet's iframe-fail state machine (try →
 * blocked on 5.5s timeout / sub-250ms synthetic refusal) catches
 * refusals at runtime and falls back gracefully.
 *
 * Founder doctrine 6 Jun 2026 evening: "I need the thing to open
 * (for all) inside the restaurant modal at the bottom like it used
 * to. This should be for all."
 *
 * Add to this set ONLY when a venue is field-verified to flash a
 * blank frame · NEVER on assumption. The original list (TableCheck,
 * Google Reserve, SevenRooms) was over-pruned and broke the in-
 * modal experience for Zuma Dubai + others. Reverted to "minimal
 * blocklist · verify per-venue at runtime".
 */
const KNOWN_NO_EMBED = new Set([
    "tablecheck",     // confirmed 4 Jun 2026 · Saido + AIN SOPH ear-test
    // Google Reserve re-added 10 Feb 2026 (BC-012 · founder ear-test) ·
    // `resolved_platform.url` for Google Reserve venues is a Google
    // Maps deep link (`maps.google.com/?cid=...`) which is X-Frame-
    // Options SAMEORIGIN refused. The 5.5s runtime timeout does catch
    // it BUT the member sees Chrome's broken-page icon for those 5.5
    // seconds · honestly worse than the new-tab button. Google Reserve
    // does not expose an iframeable widget URL · short-circuit.
    //
    // BC-020 (founder 10 Feb 2026 PM · "Google Reserve STILL failing
    // to open in a new tab"): the backend emits the platform name in
    // mixed forms — "Google Reserve" (table_search.py:1119),
    // "google_reserve" (resolver normalisation), occasionally bare
    // "google". Match every drift. The membership test below also
    // lower-cases AND replaces underscores with spaces, so any of
    // these spellings hit.
    "google reserve",
    "google_reserve",
    // SevenRooms removed 6 Jun 2026 evening · Zuma Dubai embeds fine.
]);

/**
 * BC-019 (founder catch 10 Feb 2026 · Jamavar Dubai screenshot) ·
 * The booking-sheet flow is wrong for non-iframeable providers ·
 * "google reserve is supposed to open the google link on a new tab
 *  not silent send to the concierge like this · Where a booking
 *  engine cannot be sandboxed inside it has to open that tab not
 *  render a dead link".
 *
 * Doctrine: if we CANNOT host an honest booking surface for a venue
 * (the iframe is known-blocked OR the URL is *.google.com), clicking
 * Reserve must open the provider URL in a new tab · NO booking sheet,
 * NO LC ticket creation, NO faked "Request secured" confirmation. We
 * do not fabricate confirmations we did not earn (same anti-
 * fabrication doctrine as the data plane).
 *
 * Returns true when clicking Reserve should bypass the BookingSheet
 * entirely and open `providerLink` in a new tab. The caller still
 * logs the click as `external_handoff` (so the desk knows the member
 * clicked away to book directly).
 */
export function isExternalHandoff(provider, providerLink) {
    if (!providerLink) return false;
    // BC-022 (10 Feb 2026 PM · founder ear-test): defensively gate on
    // protocol. If providerLink isn't a real `http(s)://` URL it
    // CANNOT be opened externally · 525 overlay rows shipped
    // placeholder strings like "VERIFY - use official restaurant
    // website or MICHELIN booking path" that we've since stripped
    // from DB, but the same shape can show up in any future drift.
    // Anchor refuses to render → primary falls back to Concierge®.
    const linkStr = String(providerLink || "").trim();
    if (!linkStr.toLowerCase().startsWith("http://")
        && !linkStr.toLowerCase().startsWith("https://")) {
        return false;
    }
    // BC-020 (10 Feb 2026): platform drift — backend emits "Google
    // Reserve" (space), "google_reserve" (underscore), and rarely
    // bare "google". Lower-case AND normalise underscores → spaces
    // before the set lookup so every spelling hits. Also catch any
    // platform name CONTAINING the token "google" as belt-and-braces
    // (no other platform name legitimately contains "google" today).
    const name = String(provider || "")
        .toLowerCase()
        .replace(/_/g, " ")
        .trim();
    if (KNOWN_NO_EMBED.has(name)) return true;
    if (name.includes("google")) return true;
    try {
        const host = (new URL(linkStr).hostname || "").toLowerCase();
        if (host === "google.com" || host.endsWith(".google.com")) return true;
    } catch {
        // Malformed URL · let the booking sheet error-state handle it.
    }
    return false;
}

/**
 * BC-023 (10 Feb 2026 PM · founder doctrine): "we have to open to
 * every single booking agent it connects to · fork, resy, nomad,
 * chope, swiggy whatever". Predicate · does the venue ship a
 * VERIFIED self-service URL the member could click through to?
 *
 * Returns the http(s) URL when verified, else null. Used by the
 * drawer to render the primary CTA as a real anchor for any
 * verified rail · not just Google Reserve.
 */
export function verifiedRailUrl(venue) {
    if (!venue) return null;
    const candidates = [
        venue?.resolved_platform?.url,
        venue?.verified_reservation_url,
        venue?.reservation_url,
        venue?.route_via_url,
    ];
    for (const c of candidates) {
        if (typeof c !== "string") continue;
        const s = c.trim();
        if (s.toLowerCase().startsWith("http://")
            || s.toLowerCase().startsWith("https://")) {
            return s;
        }
    }
    return null;
}

/**
 * Detect a touch / iOS device. We're deliberately conservative — we
 * only suppress the iframe when we're SURE we'd otherwise show a
 * blank white pane. Heuristic uses:
 *   - `window.matchMedia("(pointer: coarse)")` (mobile / tablet touch)
 *   - userAgent iPad / iPhone / iPod / Android sniff (belt + braces)
 */
function isMobileOrIOS() {
    if (typeof window === "undefined") return false;
    try {
        const coarse = !!window.matchMedia
            && window.matchMedia("(pointer: coarse)").matches;
        const ua = (navigator && navigator.userAgent) || "";
        const sniff = /iPhone|iPad|iPod|Android|Mobi/i.test(ua);
        return coarse || sniff;
    } catch {
        return false;
    }
}

export function providerEmbedUrl(provider, providerLink, { date, seats } = {}) {
    if (!provider || !providerLink) return null;
    const name = String(provider).toLowerCase();

    // ── Known-no-embed short-circuit (work-order v2 §7b bugfix) ──
    // The clean fallback is the whole point: no iframe attempt, no
    // flash of broken frame, just the new-tab button.
    if (KNOWN_NO_EMBED.has(name)) return null;

    // ── Mobile / iOS short-circuit (Item 19 iOS polish, 5 Jun 2026)
    // Safari ITP frequently delivers a blank white iframe for
    // Resy / OpenTable on iPhone. We hand off to a new tab instead.
    if (isMobileOrIOS()) return null;

    let target = null;

    try {
        const u = new URL(providerLink);

        // ── BC-012 hostname guard (founder ear-test 10 Feb 2026) ──
        // Any provider URL pointing at *.google.com is X-Frame-Options
        // SAMEORIGIN refused (maps.google.com / www.google.com /
        // reserve.google.com all block iframes by default). Short-
        // circuit to new-tab so the member never sees the broken-page
        // icon. The provider blocklist above catches "google reserve"
        // by platform name; this hostname guard catches the case
        // where the platform field drifted but the URL still points
        // at google.com.
        const host = (u.hostname || "").toLowerCase();
        if (host === "google.com" || host.endsWith(".google.com")) return null;

        if (name === "resy") {
            // Resy widget format: https://widgets.resy.com/#/venues/{slug}
            // The resolver may give us either the venue page
            // (resy.com/cities/{city}/venues/{slug}), the bare widget
            // URL, OR the wrapped widget URL with venueId in hash
            // fragment (`#/venues/10341`). Extract the slug deterministically
            // by checking BOTH the pathname AND the hash.
            let slug = null;
            const haystacks = [u.pathname, u.hash || ""];
            for (const h of haystacks) {
                const m = h.match(/\/venues\/([^/?#]+)/);
                if (m) { slug = m[1]; break; }
            }
            // Also accept `?venueId=NNN` (Resy's own widget wrapper)
            if (!slug) {
                const vid = u.searchParams.get("venueId");
                if (vid) slug = vid;
            }
            if (!slug) return null;
            target = new URL(`https://widgets.resy.com/#/venues/${slug}`);
            if (date) target.searchParams.set("date", date);
            if (seats) target.searchParams.set("seats", String(seats));
            return { url: target.toString(), provider: "Resy", embedKind: "widget" };
        }

        if (name === "opentable") {
            // OpenTable reserve URL works directly in some hosts. We
            // pass it verbatim · the iframe load handler is responsible
            // for detecting if OpenTable refuses to be framed (in which
            // case the per-venue verification adds it to KNOWN_NO_EMBED).
            target = new URL(providerLink);
            if (date) target.searchParams.set("dateTime", `${date}T19:30`);
            if (seats) target.searchParams.set("covers", String(seats));
            return { url: target.toString(), provider: "OpenTable", embedKind: "page" };
        }

        if (name === "sevenrooms") {
            // SevenRooms · pass the URL verbatim. Embed kind "page".
            // Per-venue verification handled by iframe-fail state
            // machine (founder ear-test 6 Jun 2026 evening).
            return { url: providerLink, provider: "SevenRooms", embedKind: "page" };
        }

        // Default · try-embed for every other platform · founder
        // doctrine 6 Jun 2026 evening ("this should be for all").
        //
        // Tabelog / Tock / TheFork / Quandoo / EazyDiner / Dineout /
        // Zomato / Swiggy Dineout / Bookatable / Eat App / ReserveGo /
        // District Dining / Google Reserve · all pass through with
        // their URL verbatim. The iframe-fail state machine catches
        // X-Frame-Options DENY at runtime · refused frames fall back
        // to new-tab. Verified-blank platforms go in KNOWN_NO_EMBED
        // above to skip the 5.5-sec flash.
        return {
            url: providerLink,
            provider: provider,
            embedKind: "page",
        };
    } catch {
        return null;
    }
}
